import React, { useEffect, useState } from "react";
import {
  motion,
  AnimatePresence,
  useReducedMotion,
} from "framer-motion";
import { YARNS, YARN_ORDER } from "../lib/yarns";

/**
 * The Loom — hand-drawn animated SVG centerpiece.
 *
 * Bold dark outlines, slightly wobbly strokes, organic yarn balls with
 * sweeping thread striations and loose tails. Pulls happen via the separate
 * PullTassel component below; here, hovering a yarn ball magnifies it and
 * surfaces a small explainer pop-up.
 */
export default function Loom({ isPulling = false, lastYarn = null }) {
  const reducedMotion = useReducedMotion();
  const [hoveredYarn, setHoveredYarn] = useState(null);

  const INK = "#3d2a14"; // warm dark brown — used for all hand-drawn outlines

  const yarnPositions = [
    { x: 145, y: 100, key: "centre", tilt: -6, tailDir: -1 },
    { x: 320, y: 80, key: "sharpen", tilt: 4, tailDir: 1 },
    { x: 500, y: 80, key: "reflect", tilt: -3, tailDir: -1 },
    { x: 675, y: 100, key: "act", tilt: 5, tailDir: 1 },
  ];

  const [unravel, setUnravel] = useState(false);
  useEffect(() => {
    if (isPulling) {
      setUnravel(true);
      const t = setTimeout(() => setUnravel(false), 700);
      return () => clearTimeout(t);
    }
  }, [isPulling]);

  return (
    <div
      className="relative w-full"
      data-testid="loom-container"
      aria-label="The Weave loom — four hand-drawn yarn balls feeding into a partially woven tapestry."
    >
      <svg
        viewBox="0 0 800 540"
        className="w-full h-auto select-none"
        role="img"
      >
        <defs>
          {/* Hand-drawn weave pattern — uneven criss-cross strokes */}
          <pattern
            id="weave-handdrawn"
            width="22"
            height="22"
            patternUnits="userSpaceOnUse"
          >
            <rect width="22" height="22" fill="#f3ead9" />
            {/* horizontal weft */}
            <path
              d="M0 7 Q 6 6, 11 7 T 22 7"
              stroke="#b59770"
              strokeWidth="1.6"
              fill="none"
              strokeLinecap="round"
              opacity="0.7"
            />
            <path
              d="M0 15 Q 6 16, 11 15 T 22 15"
              stroke="#b59770"
              strokeWidth="1.4"
              fill="none"
              strokeLinecap="round"
              opacity="0.55"
            />
            {/* vertical warp */}
            <path
              d="M7 0 Q 6 6, 7 11 T 7 22"
              stroke="#c9a878"
              strokeWidth="1.4"
              fill="none"
              strokeLinecap="round"
              opacity="0.6"
            />
            <path
              d="M15 0 Q 16 6, 15 11 T 15 22"
              stroke="#c9a878"
              strokeWidth="1.2"
              fill="none"
              strokeLinecap="round"
              opacity="0.5"
            />
          </pattern>

          <filter id="ink-shadow" x="-15%" y="-15%" width="130%" height="130%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="2" />
            <feOffset dx="1" dy="3" />
            <feComponentTransfer>
              <feFuncA type="linear" slope="0.22" />
            </feComponentTransfer>
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* ───────── Wooden loom frame — hand-drawn with wobbly outlines ───────── */}
        <g filter="url(#ink-shadow)">
          {/* Top beam */}
          <path
            d="M 78 188
               C 220 184, 380 186, 540 185
               S 720 187, 722 189
               L 720 204
               C 580 207, 380 205, 220 207
               S 80 205, 78 203
               Z"
            fill="#c8a875"
            stroke={INK}
            strokeWidth="2.4"
            strokeLinejoin="round"
          />
          {/* Bottom beam */}
          <path
            d="M 78 432
               C 220 429, 380 431, 540 430
               S 720 431, 722 433
               L 720 449
               C 580 452, 380 450, 220 452
               S 80 450, 78 448
               Z"
            fill="#c8a875"
            stroke={INK}
            strokeWidth="2.4"
            strokeLinejoin="round"
          />
          {/* Left upright */}
          <path
            d="M 78 188
               C 76 260, 80 340, 78 432
               L 92 432
               C 94 340, 90 260, 92 188
               Z"
            fill="#b89561"
            stroke={INK}
            strokeWidth="2.4"
            strokeLinejoin="round"
          />
          {/* Right upright */}
          <path
            d="M 708 188
               C 706 260, 710 340, 708 432
               L 722 432
               C 724 340, 720 260, 722 188
               Z"
            fill="#b89561"
            stroke={INK}
            strokeWidth="2.4"
            strokeLinejoin="round"
          />
          {/* wood grain ticks */}
          {[210, 240, 280, 320, 360, 400].map((y, i) => (
            <path
              key={`grain-l-${i}`}
              d={`M 82 ${y} Q 86 ${y + 4}, 88 ${y + 8}`}
              stroke={INK}
              strokeWidth="1"
              fill="none"
              opacity="0.45"
              strokeLinecap="round"
            />
          ))}
          {[220, 260, 300, 340, 380, 415].map((y, i) => (
            <path
              key={`grain-r-${i}`}
              d={`M 712 ${y} Q 716 ${y + 4}, 718 ${y + 8}`}
              stroke={INK}
              strokeWidth="1"
              fill="none"
              opacity="0.45"
              strokeLinecap="round"
            />
          ))}
          {/* Top peg/joint detail */}
          <circle cx="86" cy="196" r="2.4" fill={INK} />
          <circle cx="714" cy="196" r="2.4" fill={INK} />
          <circle cx="86" cy="440" r="2.4" fill={INK} />
          <circle cx="714" cy="440" r="2.4" fill={INK} />
        </g>

        {/* ───────── Tapestry ───────── */}
        <motion.g
          animate={unravel ? { opacity: [1, 0.5, 1] } : { opacity: 1 }}
          transition={{ duration: 0.7, ease: "easeInOut" }}
        >
          {/* Hand-drawn cloth backing with slightly wavy edges */}
          <path
            d="M 96 207
               C 200 205, 400 209, 600 206
               S 700 207, 704 208
               L 704 430
               C 600 432, 400 428, 200 431
               S 100 430, 96 429
               Z"
            fill="url(#weave-handdrawn)"
            stroke={INK}
            strokeWidth="1.6"
            strokeLinejoin="round"
            opacity="0.95"
          />

          {/* Coloured weft bands — irregular edges, hand-drawn */}
          {YARN_ORDER.map((k, idx) => {
            const yarn = YARNS[k];
            const baseY = 226 + idx * 42;
            return (
              <motion.g
                key={`band-${k}`}
                animate={
                  reducedMotion ? undefined : { opacity: [0.78, 0.95, 0.78] }
                }
                transition={{
                  duration: 4 + idx * 0.4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <path
                  d={`M 100 ${baseY}
                      C 200 ${baseY - 1}, 380 ${baseY + 2}, 560 ${baseY - 1}
                      S 700 ${baseY}, 702 ${baseY + 1}
                      L 702 ${baseY + 16}
                      C 560 ${baseY + 17}, 380 ${baseY + 14}, 200 ${baseY + 17}
                      S 100 ${baseY + 16}, 100 ${baseY + 15}
                      Z`}
                  fill={yarn.pastel}
                  stroke={INK}
                  strokeWidth="1.2"
                  strokeLinejoin="round"
                  opacity="0.92"
                />
                {/* a few woven thread strokes on top of the band */}
                {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                  <path
                    key={i}
                    d={`M ${130 + i * 75} ${baseY + 3} Q ${135 + i * 75} ${baseY + 8}, ${130 + i * 75} ${baseY + 13}`}
                    stroke={yarn.deep}
                    strokeWidth="1.2"
                    fill="none"
                    opacity="0.55"
                  />
                ))}
              </motion.g>
            );
          })}

          {/* Bottom fringe — hand-drawn, varied lengths and slight curls */}
          <g>
            {Array.from({ length: 28 }).map((_, i) => {
              const x = 110 + i * 21 + ((i * 7) % 4);
              const len = 14 + ((i * 11) % 12);
              const curl = ((i % 3) - 1) * 3;
              const colors = ["#c9a878", "#b89561", "#a98a55", "#d6b894"];
              return (
                <path
                  key={i}
                  d={`M ${x} 432 Q ${x + curl} ${432 + len * 0.5}, ${x + curl} ${432 + len}`}
                  stroke={colors[i % colors.length]}
                  strokeWidth="1.6"
                  fill="none"
                  strokeLinecap="round"
                  opacity="0.85"
                />
              );
            })}
          </g>
        </motion.g>

        {/* ───────── Threads from yarn balls into the tapestry top ───────── */}
        {yarnPositions.map(({ x, y, key }) => (
          <motion.path
            key={`thread-${key}`}
            d={`M ${x} ${y + 30}
                Q ${x - 4} ${y + 70}, ${x + 2} ${y + 110}
                T ${x} 207`}
            stroke={YARNS[key].deep}
            strokeWidth="2.2"
            fill="none"
            strokeLinecap="round"
            animate={
              reducedMotion
                ? undefined
                : { pathLength: [0.95, 1, 0.97], opacity: [0.85, 1, 0.85] }
            }
            transition={{
              duration: 3 + (key.length % 3) * 0.3,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}

        {/* ───────── Yarn balls — hand-drawn ───────── */}
        {yarnPositions.map(({ x, y, key, tilt, tailDir }, idx) => {
          const yarn = YARNS[key];
          const isHovered = hoveredYarn === key;
          return (
            <g key={`ball-${key}`} transform={`translate(${x}, ${y})`}>
              <motion.g
                initial={false}
                animate={{
                  y: reducedMotion || isHovered ? 0 : [0, -5, 0, 4, 0],
                  scale: isHovered ? 1.18 : 1,
                  rotate: tilt,
                }}
                transition={{
                  y: {
                    duration: 5.5 + idx * 0.4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  },
                  scale: { duration: 0.35, ease: [0.22, 1, 0.36, 1] },
                  rotate: { duration: 0.4 },
                }}
                style={{ originX: 0.5, originY: 0.5 }}
              >
                <YarnBall yarn={yarn} ink={INK} tailDir={tailDir} />
                {/* Invisible focus/hover hit-target */}
                <ellipse
                  cx="0"
                  cy="0"
                  rx="46"
                  ry="40"
                  fill="transparent"
                  style={{ cursor: "pointer", outline: "none" }}
                  tabIndex={0}
                  role="img"
                  aria-label={`${yarn.label}: ${yarn.promise}. Source: ${yarn.source}.`}
                  data-testid={`yarn-ball-hit-${key}`}
                  onPointerEnter={() => setHoveredYarn(key)}
                  onPointerLeave={() => setHoveredYarn(null)}
                  onFocus={() => setHoveredYarn(key)}
                  onBlur={() => setHoveredYarn(null)}
                />
              </motion.g>
              <text
                y="68"
                textAnchor="middle"
                fontSize="14"
                fontWeight="600"
                fill={INK}
                style={{ fontFamily: "Rubik, sans-serif", letterSpacing: "0.06em" }}
              >
                {yarn.label}
              </text>
            </g>
          );
        })}

        {/* Loose thread dangling below the loom — leads the eye to the tassel */}
        <motion.path
          d={`M 660 432
              Q 666 470, 678 500
              T 700 540`}
          stroke={lastYarn ? YARNS[lastYarn].deep : "#a98a55"}
          strokeWidth="2.6"
          strokeLinecap="round"
          fill="none"
          animate={
            reducedMotion ? undefined : { pathLength: [0.92, 1, 0.92] }
          }
          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
        />
      </svg>

      {/* Yarn explainer pop-ups — HTML overlays positioned over the SVG */}
      <AnimatePresence>
        {hoveredYarn && (() => {
          const pos = yarnPositions.find((p) => p.key === hoveredYarn);
          if (!pos) return null;
          const yarn = YARNS[hoveredYarn];
          const anchorRight = pos.x > 500;
          return (
            <motion.div
              key={hoveredYarn}
              initial={{ opacity: 0, y: 6, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 6, scale: 0.96 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              className="absolute pointer-events-none z-20"
              style={{
                left: `${(pos.x / 800) * 100}%`,
                top: `${(pos.y / 540) * 100}%`,
                transform: `translate(${anchorRight ? "calc(-100% + 24px)" : "calc(-24px)"}, calc(-100% - 28px))`,
              }}
              data-testid={`yarn-tooltip-${hoveredYarn}`}
            >
              <div
                className="rounded-2xl shadow-[0_12px_30px_rgba(60,40,10,0.18)] border-2 px-4 py-3 max-w-[230px]"
                style={{ backgroundColor: yarn.pastel, borderColor: "#3d2a14" }}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <span
                    className="block w-2 h-2 rounded-full"
                    style={{ backgroundColor: yarn.accent }}
                    aria-hidden
                  />
                  <span
                    className="text-[10px] uppercase tracking-[0.18em] font-semibold"
                    style={{ color: yarn.accent }}
                  >
                    {yarn.label}
                  </span>
                </div>
                <p className="text-sm font-medium text-[#1a1a1a] leading-snug">
                  {yarn.promise}
                </p>
                <p className="text-[11px] text-[#3a3a3a]/75 mt-1.5 leading-snug">
                  {yarn.source}
                </p>
              </div>
              <div
                className="absolute"
                style={{
                  left: anchorRight ? "auto" : "20px",
                  right: anchorRight ? "20px" : "auto",
                  bottom: "-9px",
                  width: 0,
                  height: 0,
                  borderLeft: "9px solid transparent",
                  borderRight: "9px solid transparent",
                  borderTop: `9px solid #3d2a14`,
                }}
                aria-hidden
              />
              <div
                className="absolute"
                style={{
                  left: anchorRight ? "auto" : "21px",
                  right: anchorRight ? "21px" : "auto",
                  bottom: "-6px",
                  width: 0,
                  height: 0,
                  borderLeft: "8px solid transparent",
                  borderRight: "8px solid transparent",
                  borderTop: `8px solid ${yarn.pastel}`,
                }}
                aria-hidden
              />
            </motion.div>
          );
        })()}
      </AnimatePresence>
    </div>
  );
}

/**
 * YarnBall — a hand-drawn ball of yarn.
 *
 * Geometry is centered at (0,0) so the parent translate places it. The shape
 * is a slightly imperfect oval with a bold dark outline, sweeping thread
 * striations following the ball's contour, and a loose tail off to one side.
 */
function YarnBall({ yarn, ink, tailDir = 1 }) {
  // Striation arcs — generated to look like wound thread wrapping the oval
  // from many angles. Each stroke is a quadratic bezier across the ball.
  const arcs = [
    { d: "M -30 -8 Q 0 -22, 30 -6", w: 1.4, op: 0.7 },
    { d: "M -32 0 Q 0 -16, 32 2", w: 1.2, op: 0.55 },
    { d: "M -32 8 Q 0 -8, 32 10", w: 1.3, op: 0.6 },
    { d: "M -30 16 Q 0 0, 30 18", w: 1.2, op: 0.5 },
    { d: "M -26 22 Q 0 8, 26 24", w: 1, op: 0.45 },
    // counter-direction strands suggesting a wound ball
    { d: "M -28 -12 Q 4 0, 22 22", w: 1.1, op: 0.45 },
    { d: "M -22 -20 Q 8 -4, 26 18", w: 1, op: 0.4 },
    { d: "M -22 22 Q 4 0, 28 -16", w: 1, op: 0.4 },
    { d: "M 22 -22 Q -4 0, -26 18", w: 1, op: 0.4 },
    // a few short cross-strokes on the right cap
    { d: "M 18 -16 Q 26 -10, 30 -2", w: 1.1, op: 0.55 },
    { d: "M -18 -18 Q -26 -10, -30 0", w: 1.1, op: 0.55 },
    { d: "M -16 18 Q -22 24, -26 26", w: 1, op: 0.45 },
    { d: "M 16 18 Q 22 24, 26 26", w: 1, op: 0.45 },
  ];

  const tailX = 28 * tailDir;
  const tailControl1X = (28 + 14) * tailDir;
  const tailControl2X = (28 + 22) * tailDir;
  const tailEndX = (28 + 30) * tailDir;

  return (
    <g filter="url(#ink-shadow)">
      {/* Main oval body — slightly imperfect (drawn via path for wobble) */}
      <path
        d="M 0 -28
           C 18 -28, 32 -16, 32 -2
           C 33 12, 22 26, 4 28
           C -16 28, -32 16, -32 0
           C -32 -14, -18 -28, 0 -28
           Z"
        fill={yarn.pastel}
        stroke={ink}
        strokeWidth="2.4"
        strokeLinejoin="round"
      />
      {/* Inner darker rim (very light) — adds depth */}
      <path
        d="M -26 6 Q -10 22, 18 24"
        stroke={yarn.accent}
        strokeWidth="1.2"
        fill="none"
        opacity="0.35"
        strokeLinecap="round"
      />

      {/* Thread striations */}
      {arcs.map((a, i) => (
        <path
          key={i}
          d={a.d}
          stroke={yarn.accent}
          strokeWidth={a.w}
          fill="none"
          opacity={a.op}
          strokeLinecap="round"
        />
      ))}

      {/* Highlight blob — soft white sheen */}
      <ellipse
        cx="-10"
        cy="-12"
        rx="7"
        ry="3.5"
        fill="#ffffff"
        opacity="0.32"
      />

      {/* Loose thread tail trailing off to the side */}
      <path
        d={`M ${tailX * 0.7} ${tailDir > 0 ? 8 : 8}
            C ${tailControl1X} ${10 + tailDir * 4},
              ${tailControl2X} ${22 + tailDir * 6},
              ${tailEndX} ${30 + tailDir * 8}`}
        stroke={yarn.deep}
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
      />
      {/* tiny end fluff on the tail */}
      <circle
        cx={tailEndX}
        cy={30 + tailDir * 8}
        r="2.2"
        fill={yarn.deep}
        stroke={ink}
        strokeWidth="0.8"
      />
    </g>
  );
}
